import { ActionReducer, ActionReducerMap, MetaReducer } from '@ngrx/store';
import * as fromAuth from './base-auth.reducer';
import * as fromShared from './base-shared.reducer';
import * as fromAdministration from './base-administration.reducer';
import * as fromForms from './base-forms.reducer';
import * as fromAssignments from './base-assignments.reducer';
import * as fromCandidates from './base-candidates.reducer';
import * as fromCustomerDepartment from './base-customer-department.reducer';
import * as fromLocationWorkingTime from './base-location-working-time.reducer';
import * as fromCustomerDashboard from './base-dashboard-customer.reducer';
import * as fromPspDashboard from './base-dashboard-psp.reducer';
import * as fromDocuments from './base-documents.reducer';
import * as fromInquires from './base-inquiry.reducer';
import * as fromOffers from './base-offers.reducer';
import * as fromOffersAndBookings from './base-offers-and-bookings.reducer';
import * as fromPspBranch from './base-psp-branch.reducer';
import * as fromPspPriceList from './base-psp-price-list.reducer';
import * as fromRelatedCustomerLocations from './base-related-customer-locations.reducer';
import * as fromRelatedPspBranches from './base-related-psp-branches.reducer';
import * as fromTimeRecords from './base-time-records.reducer';
import * as fromUsers from './base-user.reducer';
import * as fromListFilters from './base-list-filters.reducer';
import * as fromAbsenceRequests from './base-absence-requests.reducer';
import { localStorageSync } from 'ngrx-store-localstorage';
import { resetStore } from '../actions/base.actions';

export interface BaseCoreState {
  auth: fromAuth.BaseAuthState;
  shared: fromShared.SharedState;
  administration: fromAdministration.AdministrationState;
  forms: fromForms.BaseFormsState;
  listFilters: fromListFilters.ListFiltersState;
}

export interface AppState extends BaseCoreState {
  assignments: fromAssignments.AssignmentsState;
  candidates: fromCandidates.CandidatesState;
  customerDepartment: fromCustomerDepartment.CustomerDepartmentState;
  locationWorkingTime: fromLocationWorkingTime.LocationWorkingTimeState;
  customerDashboard: fromCustomerDashboard.CustomerDashboardState;
  pspDashboard: fromPspDashboard.PspDashboardState;
  documents: fromDocuments.DocumentsState;
  inquiries: fromInquires.InquiriesState;
  offers: fromOffers.OffersState;
  offersAndBookings: fromOffersAndBookings.OffersAndBookingsState;
  pspBranch: fromPspBranch.PspBranchState;
  pspPriceList: fromPspPriceList.BasePspPriceListState;
  relatedCustomerLocations: fromRelatedCustomerLocations.BaseRelatedCustomerLocationsState;
  relatedPspBranches: fromRelatedPspBranches.RelatedPspBranchesState;
  timeRecords: fromTimeRecords.BaseTimeRecordsState;
  users: fromUsers.BaseUsersState;
  absenceRequests: fromAbsenceRequests.BaseAbsenceRequestsState;
}

export const BASE_REDUCERS: ActionReducerMap<BaseCoreState> = {
  auth: fromAuth.baseAuthReducer,
  shared: fromShared.baseSharedReducer,
  administration: fromAdministration.baseAdministrationReducer,
  forms: fromForms.baseFormsReducer,
  listFilters: fromListFilters.BaseListFiltersReducer,
};

export const INITIAL_STATE: AppState = {
  auth: fromAuth.BASE_INITIAL_AUTH_STATE,
  shared: fromShared.INITIAL_SHARED_STATE,
  administration: fromAdministration.INITIAL_USER_PROFILE_STATE,
  forms: fromForms.INITIAL_FORMS_STATE,
  listFilters: fromListFilters.INITIAL_LIST_FILTERS_STATE,
  assignments: fromAssignments.ASSIGNMENTS_INITIAL_STATE,
  candidates: fromCandidates.CANDIDATES_INIT_STATE,
  customerDepartment: fromCustomerDepartment.INITIAL_CUSTOMER_DEPARTMENT_STATE,
  locationWorkingTime: fromLocationWorkingTime.INITIAL_LOCATION_WORKING_TIME_STATE,
  customerDashboard: fromCustomerDashboard.CUSTOMER_DASHBOARD_INITIAL_STATE,
  pspDashboard: fromPspDashboard.PSP_DASHBOARD_INITIAL_STATE,
  documents: fromDocuments.INITIAL_DOCUMENTS_STATE,
  inquiries: fromInquires.INQUIRIES_INIT_STATE,
  offers: fromOffers.OFFERS_INIT_STATE,
  offersAndBookings: fromOffersAndBookings.OFFERS_AND_BOOKINGS_INITIAL_STATE,
  pspBranch: fromPspBranch.INITIAL_PSP_BRANCH_STATE,
  pspPriceList: fromPspPriceList.INITIAL_PRICE_LIST_STATE,
  relatedCustomerLocations: fromRelatedCustomerLocations.RELATED_CUSTOMER_LOCATIONS_INITIAL_STATE,
  relatedPspBranches: fromRelatedPspBranches.RELATED_PSP_BRANCHES_INITIAL_STATE,
  timeRecords: fromTimeRecords.INITIAL_TIME_RECORDS_STATE,
  users: fromUsers.INITIAL_USERS_STATE,
  absenceRequests: fromAbsenceRequests.ABSENCE_REQUESTS_INITIAL_STATE,
};

export function localStorageSyncReducer(reducer: ActionReducer<BaseCoreState>): ActionReducer<BaseCoreState> {
  return localStorageSync({
    keys: ['forms', { shared: ['isSidebarInitialOpening'] }, 'listFilters'],
    rehydrate: true,
    storage: sessionStorage,
  })(reducer);
}

export function resetStoreMetaReducer(reducer: ActionReducer<BaseCoreState>): ActionReducer<BaseCoreState> {
  return (state, action) => {
    if (action.type === resetStore.type) {
      return {
        ...INITIAL_STATE,
        shared: {
          ...INITIAL_STATE.shared,
          language: state?.shared.language || INITIAL_STATE.shared.language,
        },
      };
    }
    return reducer(state, action);
  };
}

export const BASE_META_REDUCERS: MetaReducer<BaseCoreState>[] = [localStorageSyncReducer, resetStoreMetaReducer];
